import { useEffect, useState } from 'react';
import { useInView } from '../hooks/useInView';

const activities = [
  { name:'Intervention Planning', score:4.70, rank:1,
    desc:'Students designed real community-level solutions. The highest-rated activity — because it made justice tangible, not theoretical.' },
  { name:'Problem Solving (6W+2H)', score:4.52, rank:2,
    desc:'A structured framework: Who, What, Where, When, Why, Which, How, How much. Gave students a replicable tool they can use beyond the workshop.' },
  { name:'Case Study Reading', score:4.37, rank:3,
    desc:'Real cases, real people, real outcomes. Students engaged with justice failures that felt personal — not distant.' },
  { name:'River of Life', score:4.35, rank:4,
    desc:'A reflective activity tracing personal journeys. Multiple students named it their most memorable moment of the workshop.' },
  { name:'Clay Activity', score:4.31, rank:5,
    desc:'Students shaped their core values using clay. Making the abstract physical — widely cited in their own words as transformative.' },
];

function ActivityLine({ activity, active, i }) {
  const [w, setW] = useState(0);
  const pct = (activity.score / 5) * 100;
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => setW(pct), i * 150 + 300);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <div className="nb-card" style={{ padding:'1.5rem 1.75rem', display:'flex', flexDirection:'column', gap:'0.75rem' }}>
      {/* Top row */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'1rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <div style={{ width:28, height:28, border:'2.5px solid var(--black)', borderRadius:2, background:'var(--black)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-head)', fontSize:'0.7rem', fontWeight:800, color:'var(--accent)', flexShrink:0 }}>
            #{activity.rank}
          </div>
          <span style={{ fontFamily:'var(--font-head)', fontSize:'0.95rem', fontWeight:700 }}>{activity.name}</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'6px', flexShrink:0 }}>
          <span style={{ fontFamily:'var(--font-head)', fontSize:'1.4rem', fontWeight:700, color:'var(--accent)' }}>{activity.score}</span>
          <span style={{ fontFamily:'var(--font-head)', fontSize:'0.7rem', color:'var(--muted)' }}>/5</span>
        </div>
      </div>

      {/* Sleek line with dot */}
      <div style={{ position:'relative', height:'16px', display:'flex', alignItems:'center' }}>
        {/* Track */}
        <div style={{ position:'absolute', left:0, right:0, height:'2px', background:'var(--gray-light)' }} />
        {/* Fill */}
        <div style={{
          position:'absolute', left:0, height:'2px',
          width:`${w}%`, background:'var(--accent)',
          transition:'width 1s cubic-bezier(0.25,1,0.5,1)',
        }} />
        {/* Dot */}
        <div style={{
          position:'absolute',
          left:`${w}%`, transform:'translateX(-50%)',
          width:12, height:12, borderRadius:'50%',
          background:'var(--accent)', border:'2.5px solid var(--black)',
          transition:'left 1s cubic-bezier(0.25,1,0.5,1)',
          zIndex:1,
        }} />
        {/* Scale labels */}
        {[1,2,3,4,5].map(n => (
          <div key={n} style={{ position:'absolute', left:`${((n-1)/4)*100}%`, transform:'translateX(-50%)', top:'10px', fontFamily:'var(--font-head)', fontSize:'0.52rem', color:'#CCC', fontWeight:600 }}>{n}</div>
        ))}
      </div>

      {/* Description */}
      <p style={{ fontFamily:'var(--font-body)', fontSize:'0.8rem', color:'var(--muted)', lineHeight:1.6 }}>{activity.desc}</p>
    </div>
  );
}

export default function ActivitiesSection() {
  const [ref, inView] = useInView({ threshold:0.08 });
  return (
    <section id="activities" className="section">
      <div className="container" ref={ref}>

        <div className={`reveal ${inView?'visible':''}`} style={{ marginBottom:'3.5rem' }}>
          <p className="eyebrow">Activity Effectiveness</p>
          <h2 className="h2">What moved<br />the needle</h2>
          <p className="body-lg" style={{ maxWidth:'560px', marginTop:'0.75rem' }}>
            Students rated each activity out of 5. Every single activity scored above 4. These five stood out for their ability to shift how students think, not just what they know.
          </p>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:'1rem', maxWidth:'780px' }}>
          {activities.map((a, i) => (
            <div key={i} className={`reveal ${inView?'visible':''}`} style={{ transitionDelay:`${i*0.08}s` }}>
              <ActivityLine activity={a} active={inView} i={i} />
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className={`reveal ${inView?'visible':''}`} style={{ marginTop:'2rem', transitionDelay:'0.5s', maxWidth:'780px' }}>
          <div className="nb-card-surface" style={{ padding:'1.25rem 1.5rem', display:'flex', gap:'12px', alignItems:'flex-start' }}>
            <span style={{ fontSize:'1.2rem', flexShrink:0 }}>💡</span>
            <p style={{ fontFamily:'var(--font-body)', fontSize:'0.8rem', color:'var(--muted)', lineHeight:1.6 }}>
              <strong style={{ color:'var(--black)' }}>Noteworthy:</strong> The AI activity (4.07) and Laptop activity (4.09) scored lowest — yet still above 4. This suggests technology-based activities need more integration time. Physical and reflective activities consistently created the deepest learning moments.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
